/* eslint-disable react/require-default-props */
import React, {ReactNode, useEffect, useRef} from 'react';
import {
  AccessibilityInfo,
  findNodeHandle,
  InteractionManager,
  PixelRatio,
  Platform,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import {isEmpty, isFunction} from 'lodash';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import {Modal} from '/constant/TestIDs';
import {withSafeArea} from '/provider/SafeAreaProvider';
import {getTextFromReactNode} from '/util/accessibility.util';
import {getFontFamily} from '/util/text.util';

import {styles} from './ModalHeader.style';

interface ModalHeaderProps {
  style?: {
    base?: ViewStyle;
    headerText?: TextStyle;
    actionButton?: ViewStyle;
    actionText?: TextStyle;
    actionIcon?: ViewStyle;
  };
  title?: ReactNode;
  onClose?: () => void;
  actionLabel?: ReactNode;
  onActionPress?: () => void;
  safeAreaInsets?: {top: number};
  children?: ReactNode;
  testID?: string;
  accessibilityLabel?: string;
  titleVariant?: string;
  closeIconName?: string;
  isProfileScreen?: boolean;
  accessibilityRole?: string;
  titleWrapperAccessible?: boolean;
  accessibilityHint?: string;
  actionAccessibilityLabel?: string;
  titlePressEnabled?: boolean;
}

const ACTION_MAX_FONT_SIZE_MULTIPLIER = 2;

const TITLE_MAX_FONT_SIZE_MULTIPLIER = 1.5;
export const ModalHeader: React.FC<ModalHeaderProps> = ({
  style: styleProp = {},
  title = null,
  onClose = undefined,
  actionLabel = null,
  onActionPress = null,
  safeAreaInsets = withSafeArea.safeAreaInsetsDefaultProp,
  children = null,
  titleVariant = null,
  closeIconName = null,
  isProfileScreen = false,
  accessibilityLabel = null,
  accessibilityRole = null,
  titleWrapperAccessible = undefined,
  actionAccessibilityLabel = null,
  titlePressEnabled = true,
}) => {
  const closeButtonRef = useRef<any>(null);

  useEffect(() => {
    if (!isFunction(onClose)) {
      return;
    }

    const task = InteractionManager.runAfterInteractions(() => {
      requestAnimationFrame(() => {
        const node = findNodeHandle(closeButtonRef.current);

        if (node) {
          AccessibilityInfo.setAccessibilityFocus(node);
        }
      });
    });

    return () => {
      task.cancel?.();
    };
  }, []);

  const iconFontScale = PixelRatio.getFontScale();
  const actionFontScale = PixelRatio.getFontScale();

  const dynamicIconSize = Math.min(26 * Math.max(iconFontScale, 1), 34);
  const dynamicTouchSize = Math.min(44 * Math.max(iconFontScale, 1), 56);

  const dynamicActionButtonHeight = actionFontScale > 1.2 ? 48 : 36;
  const dynamicActionButtonPaddingVertical = actionFontScale > 1.2 ? 8 : 4;
  const actionIconStyle = (styleProp?.actionIcon || {}) as ViewStyle;
  const explicitActionIconFontSize =
    typeof actionIconStyle.fontSize === 'number'
      ? actionIconStyle.fontSize
      : undefined;

  const containerStyle = {
    ...styles.base,
    paddingTop: (safeAreaInsets || {}).top,
    ...(styleProp.base || {}),
  };

  const boldTextStyle = {...getFontFamily('bold')};

  const safeText = (() => {
    try {
      const raw = getTextFromReactNode(title);
      return typeof raw === 'string' ? raw : '';
    } catch (e) {
      return '';
    }
  })();

  const renderTitleText = (accessible: boolean) => (
    <Text
      testID={Modal.modalHeader}
      variant={titleVariant || 'button'}
      style={[styles.headerText, styleProp.headerText]}
      allowFontScaling={true}
      maxFontSizeMultiplier={TITLE_MAX_FONT_SIZE_MULTIPLIER}
      numberOfLines={1}
      accessible={accessible}
      importantForAccessibility={accessible ? undefined : 'no'}
      accessibilityRole={accessible ? 'header' : undefined}
      accessibilityLabel={
        accessible ? accessibilityLabel || safeText : undefined
      }>
      {title}
    </Text>
  );

  const profileTitleWebStyle =
    Platform.OS === 'web' && isProfileScreen ? styles.profileTitleWeb : null;
  const profileTitleWrapperStyle = [
    styles.profileTitle,
    ...(Platform.OS === 'web' && isProfileScreen ? [] : [styles.titleWrapper]),
    profileTitleWebStyle,
  ];
  const backButtonWebStyle =
    Platform.OS === 'web' && closeIconName === 'chevron-left'
      ? styles.backButtonWeb
      : null;
  const backHeaderActionsWebStyle =
    Platform.OS === 'web' && closeIconName === 'chevron-left'
      ? styles.backHeaderActionsWeb
      : null;
  const backHeaderGutterSize =
    Platform.OS === 'web' && closeIconName === 'chevron-left' ? 0 : 20;
  const closeButtonSizeStyle = {
    minWidth: dynamicTouchSize,
    minHeight: dynamicTouchSize,
  };
  const actionIconFontSizeStyle = {
    fontSize: explicitActionIconFontSize ?? dynamicIconSize,
  };
  const actionButtonDynamicStyle = {
    minHeight: dynamicActionButtonHeight,
    paddingVertical: dynamicActionButtonPaddingVertical,
  };
  const iconWrapperStyle = Platform.OS === 'web' ? styles.iconWrapperWeb : null;
  const actionsStyle = backHeaderActionsWebStyle
    ? [styles.actions, backHeaderActionsWebStyle]
    : styles.actions;

  return (
    <View style={containerStyle}>
      <Gutter size={backHeaderGutterSize}>
        <View style={actionsStyle}>
          {isFunction(onClose) && (
            <TouchableOpacity
              ref={closeButtonRef}
              accessible={true}
              focusable={true}
              testID={
                closeIconName === 'close' ? 'close-button' : 'back-button'
              }
              accessibilityRole="button"
              accessibilityLabel={
                closeIconName === 'close' ? 'Close' : 'Go back'
              }
              onPress={onClose}
              style={[
                styles.closeButton,
                closeButtonSizeStyle,
                backButtonWebStyle,
              ]}>
              <View
                style={iconWrapperStyle}
                accessible={false}
                importantForAccessibility="no-hide-descendants">
                <Icon
                  testID="close-button-icon"
                  style={[
                    styles.actionIcon,
                    actionIconStyle,
                    actionIconFontSizeStyle,
                  ]}
                  name={closeIconName || 'close'}
                  accessible={false}
                  importantForAccessibility="no-hide-descendants"
                />
              </View>
            </TouchableOpacity>
          )}

          {!isEmpty(title) && !isProfileScreen && (
            <View style={styles.titleWrapper}>
              {Platform.OS === 'web' ? (
                renderTitleText(true)
              ) : (
                <KeyboardFocusable
                  accessible={true}
                  accessibilityRole="header"
                  accessibilityLabel={accessibilityLabel || safeText}
                  focusable={true}>
                  {renderTitleText(false)}
                </KeyboardFocusable>
              )}
            </View>
          )}

          {!isEmpty(title) &&
            isProfileScreen &&
            (titlePressEnabled ? (
              <TouchableOpacity
                testID={`${Modal.modalHeader}-wrapper`}
                onPress={onClose}
                style={profileTitleWrapperStyle}
                accessible={
                  titleWrapperAccessible !== undefined
                    ? titleWrapperAccessible
                    : Platform.OS === 'ios'
                }>
                {renderTitleText(true)}
              </TouchableOpacity>
            ) : (
              <View
                testID={`${Modal.modalHeader}-wrapper`}
                style={profileTitleWrapperStyle}
                accessible={false}>
                {renderTitleText(true)}
              </View>
            ))}

          <View style={styles.actionFiller} />

          {!isEmpty(actionLabel) && isFunction(onActionPress) && (
            <TouchableOpacity
              focusable={true}
              accessible={true}
              accessibilityRole={accessibilityRole || 'button'}
              accessibilityLabel={
                actionAccessibilityLabel || getTextFromReactNode(actionLabel)
              }
              testID={Modal.modalActionButton}
              onPress={onActionPress}
              style={[
                styles.actionButton,
                styleProp.actionButton,
                actionButtonDynamicStyle,
              ]}>
              <Text
                allowFontScaling={true}
                maxFontSizeMultiplier={ACTION_MAX_FONT_SIZE_MULTIPLIER}
                numberOfLines={1}
                style={[boldTextStyle, styles.actionText, styleProp.actionText]}
                accessible={false}>
                {actionLabel}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </Gutter>

      {children}
    </View>
  );
};

export default withSafeArea(ModalHeader);
